import { useCallback } from 'react';
import { checkNetwork as checkNetworkUtil } from '@smart-tv/utils/kmm/check-network';
import { atom, useAtom } from 'jotai';

/**
 * Custom hook for managing network connectivity state.
 *
 * Note: Tizen's webapis.network.getActiveConnectionType() is unreliable when the router
 * loses internet connection (TV reports as online even when router has no internet).
 * Therefore, we always perform an actual connectivity test via fetch.
 *
 * @returns Object containing network state and utility functions
 */
export const useNetwork = () => {
  const [isOffline, setIsOffline] = useAtom(isOfflineAtom);
  const [isNetworkDialogOpen, setIsNetworkDialogOpen] = useAtom(isNetworkDialogOpenAtom);
  const [isNetworkDialogSuppressed, setIsNetworkDialogSuppressed] = useAtom(
    isNetworkDialogSuppressedAtom
  );

  const checkNetwork = useCallback(async (): Promise<boolean> => {
    return checkNetworkUtil();
  }, []);

  const checkAndUpdateNetwork = useCallback(async (): Promise<boolean> => {
    const hasNetwork = await checkNetwork();
    setIsOffline(!hasNetwork);
    if (hasNetwork) setIsNetworkDialogSuppressed(false);
    return hasNetwork;
  }, [checkNetwork, setIsOffline, setIsNetworkDialogSuppressed]);

  return {
    isOffline,
    setIsOffline,
    isNetworkDialogOpen,
    setIsNetworkDialogOpen,
    isNetworkDialogSuppressed,
    setIsNetworkDialogSuppressed,
    checkNetwork,
    checkAndUpdateNetwork
  };
};

export const isOfflineAtom = atom<boolean>(false);
export const isNetworkDialogOpenAtom = atom<boolean>(false);
export const isNetworkDialogSuppressedAtom = atom<boolean>(false);

export default useNetwork;
